import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

const OTP_EXPIRY_SECONDS = 5 * 60;

function ForgotPasswordPage() {

  // step: 'request' -> 'code' (email flow) -> 'admin' (no email on file) -> 'done'
  const [step, setStep] = useState('request');

  const [username, setUsername] = useState('');
  const [phone, setPhone]       = useState('');
  const [code, setCode]         = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword]       = useState(false);

  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(OTP_EXPIRY_SECONDS);

  useEffect(() => {
    if (step !== 'code') return;
    setSecondsLeft(OTP_EXPIRY_SECONDS);
    const timer = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [step]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const seconds = String(secondsLeft % 60).padStart(2, '0');
  const expired = step === 'code' && secondsLeft === 0;

  async function handleRequestCode() {
    if (!username.trim() || !phone.trim()) {
      setError('Please enter both your username and phone number.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), phone: phone.trim() }),
      });
      const data = await response.json();
      setStep(data.method === 'email' ? 'code' : 'admin');
    } catch (err) {
      setError('Could not reach the server. Is Flask running?');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword() {
    if (!code.trim()) {
      setError('Please enter the 6-digit code sent to your email.');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          code: code.trim(),
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Could not reset your password.');
        setLoading(false);
        return;
      }

      setStep('done');
    } catch (err) {
      setError('Could not reach the server. Is Flask running?');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card} className="card-lift">

        <div style={styles.logoCircle}>🔑</div>
        <h2 style={styles.title}>Forgot Password</h2>

        {step === 'request' && (
          <>
            <p style={styles.subtitle}>
              Enter your username and the phone number on your account.
            </p>

            <div style={styles.fieldGroup}>
              <p style={styles.fieldLabel}>Username</p>
              <input
                id="forgot-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') document.getElementById('forgot-phone').focus();
                }}
                placeholder="Enter your username"
                style={styles.input}
                className="input-field"
                autoFocus
              />
            </div>

            <div style={styles.fieldGroup}>
              <p style={styles.fieldLabel}>Phone Number</p>
              <input
                id="forgot-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRequestCode(); }}
                placeholder="e.g. 0712 345 678"
                style={styles.input}
                className="input-field"
              />
            </div>

            {error !== '' && (
              <p style={styles.errorMsg}>{error}</p>
            )}

            <button
              onClick={handleRequestCode}
              disabled={loading}
              style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
              className="btn-lift"
            >
              {loading ? 'Sending...' : 'Send Code'}
            </button>

            <div style={styles.bottomLinks}>
              <a href="/login" style={styles.linkAnchor} className="link-underline">
                ← Back to Login
              </a>
            </div>
          </>
        )}

        {step === 'code' && (
          <>
            <p style={styles.subtitle}>
              We've emailed a 6-digit code to the email on file for this account.
              Enter it below along with your new password.
            </p>

            <p style={{ ...styles.timer, color: expired ? 'var(--color-danger)' : 'var(--color-primary)' }}>
              {expired ? 'Code expired' : `Code expires in ${minutes}:${seconds}`}
            </p>

            <div style={styles.fieldGroup}>
              <p style={styles.fieldLabel}>6-Digit Code</p>
              <input
                id="reset-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                style={{ ...styles.input, letterSpacing: '4px', textAlign: 'center', fontSize: '20px' }}
                className="input-field"
                autoFocus
              />
            </div>

            <div style={styles.fieldGroup}>
              <p style={styles.fieldLabel}>New Password</p>
              <div style={styles.passwordRow}>
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={styles.passwordInput}
                  className="input-field"
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.showBtn}
                  className="btn-lift"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <p style={styles.fieldLabel}>Confirm New Password</p>
              <input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResetPassword(); }}
                placeholder="Re-enter new password"
                style={styles.input}
                className="input-field"
              />
            </div>

            {error !== '' && (
              <p style={styles.errorMsg}>{error}</p>
            )}

            <button
              onClick={handleResetPassword}
              disabled={loading || expired}
              style={{ ...styles.submitBtn, opacity: (loading || expired) ? 0.6 : 1 }}
              className="btn-lift"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div style={styles.bottomLinks}>
              <a
                href="/forgot-password"
                onClick={(e) => { e.preventDefault(); setStep('request'); setCode(''); setError(''); }}
                style={styles.linkAnchor}
                className="link-underline"
              >
                ← Request a new code
              </a>
            </div>
          </>
        )}

        {step === 'admin' && (
          <>
            <p style={styles.subtitle}>
              If those details match an account, your request has been sent.
              Your admin or agent will reach out with a new password shortly.
            </p>
            <a href="/login" style={styles.backAnchor} className="link-underline">
              ← Back to Login
            </a>
          </>
        )}

        {step === 'done' && (
          <>
            <p style={styles.subtitle}>
              Your password has been reset successfully. You can now log in with your new password.
            </p>
            <a href="/login" style={styles.backAnchor} className="link-underline">
              ← Back to Login
            </a>
          </>
        )}

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at 15% 10%, var(--color-primary-soft-2) 0%, var(--color-bg) 45%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: 'var(--font-sans)',
  },
  card: {
    background: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid var(--color-border)',
    textAlign: 'center',
    animation: 'fadeInUp 0.4s ease',
  },
  logoCircle: {
    width: '76px',
    height: '76px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--color-primary-light), var(--color-primary-soft))',
    boxShadow: '0 0 0 6px var(--color-primary-light)',
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '27px',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    margin: '0 0 8px',
    color: 'var(--color-ink)',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--color-muted)',
    margin: '0 0 24px',
    lineHeight: 1.6,
  },
  timer: {
    fontSize: '14px',
    fontWeight: 700,
    margin: '0 0 20px',
  },
  fieldGroup: {
    marginBottom: '20px',
    textAlign: 'left',
  },
  fieldLabel: {
    fontSize: '13px',
    color: 'var(--color-ink-soft)',
    margin: '0 0 8px',
    fontWeight: 600,
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '1.5px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '15px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-ink)',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  passwordRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  passwordInput: {
    flex: 1,
    padding: '14px 16px',
    border: '1.5px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '15px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-ink)',
  },
  showBtn: {
    padding: '14px 18px',
    background: 'var(--color-bg)',
    border: '1.5px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '13px',
    color: 'var(--color-ink-soft)',
    fontWeight: 600,
  },
  errorMsg: {
    color: 'var(--color-danger)',
    fontSize: '13px',
    margin: '0 0 16px',
    textAlign: 'center',
    padding: '10px',
    backgroundColor: 'var(--color-danger-light)',
    borderRadius: 'var(--radius-sm)',
  },
  submitBtn: {
    width: '100%',
    padding: '16px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-brand)',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontSize: '16px',
    fontWeight: 700,
    marginBottom: '24px',
    boxShadow: '0 6px 16px rgba(22, 121, 74, 0.28)',
  },
  bottomLinks: {
    paddingTop: '20px',
    borderTop: '1px solid var(--color-border)',
    textAlign: 'center',
  },
  linkAnchor: {
    fontSize: '14px',
    color: 'var(--color-primary)',
    fontWeight: 700,
  },
  backAnchor: {
    fontSize: '14px',
    color: 'var(--color-primary)',
    fontWeight: 700,
  },
};

export default ForgotPasswordPage;
